import treasuryChartResize from './treasuryChartResize';
import TreasuryDialog from './TreasuryDialog';
import TreasuryTable from './TreasuryTable';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchAPI, postAPI, putAPI, deleteAPI } from '../../utils/utils';
import { can } from '../../utils/permissions';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './SponsorsTab.css';
import { formatMoney } from '../../utils/money';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

// Cadence configuration for recurring sponsorships
const CADENCE_OPTIONS = [
    { value: 'monthly', label: 'Mensual', months: 1, perLabel: '/mes' },
    { value: 'quarterly', label: 'Trimestral', months: 3, perLabel: '/trimestre' },
    { value: 'quadrimestral', label: 'Quadrimestral', months: 4, perLabel: '/quadrimestre' },
    { value: 'semiannual', label: 'Semestral', months: 6, perLabel: '/semestre' },
    { value: 'annual', label: 'Anual', months: 12, perLabel: '/any' }
];

const getCadenceOption = (cadence) => CADENCE_OPTIONS.find(o => o.value === cadence) || CADENCE_OPTIONS[0];

/**
 * SponsorsTab - Manages external sponsors (patrocinadors)
 * 
 * Simplified flow:
 * 1. Create a tier with pricing
 * 2. Generate a payment link for the tier
 * 3. Share the link with potential sponsors
 * 4. When they pay, sponsor is auto-created and subscription activated
 * 5. Admin can edit sponsor details afterwards
 */
function SponsorsTab({ userInfo, stripeConnectStatus }) {
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedTier, setExpandedTier] = useState(new Set());
    const [tierSubscriptions, setTierSubscriptions] = useState({});
    const [loadingSubscriptions, setLoadingSubscriptions] = useState({});
    const [actionLoading, setActionLoading] = useState(null);
    const [monthlyRevenue, setMonthlyRevenue] = useState(null);
    
    // Create tier form state
    const [showCreateTierForm, setShowCreateTierForm] = useState(false);
    const [tierForm, setTierForm] = useState({ nom: '', descripcio: '', import: '', commission_mode: 'colla_absorbs', cadence: 'monthly' });
    const [tierFormLoading, setTierFormLoading] = useState(false);
    const [tierFormError, setTierFormError] = useState(null);
    const [createSuccess, setCreateSuccess] = useState(null);

    // Edit tier state
    const [editingTierId, setEditingTierId] = useState(null);
    const [editTierName, setEditTierName] = useState('');
    const [editTierLoading, setEditTierLoading] = useState(false);

    // Delete tier state
    const [deleteTierLoading, setDeleteTierLoading] = useState(null);

    // Payment link modal state
    const [paymentLinkModal, setPaymentLinkModal] = useState({ visible: false, link: '', tierName: '' });
    const [copySuccess, setCopySuccess] = useState(false);
    const [generateLinkLoading, setGenerateLinkLoading] = useState(null);

    // History state
    const [showHistory, setShowHistory] = useState(true);
    const [historyEvents, setHistoryEvents] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(0);
    const HISTORY_PAGE_SIZE = 20;

    // Edit sponsor modal state
    const [editingSponsor, setEditingSponsor] = useState(null);
    const [editSponsorForm, setEditSponsorForm] = useState({ nom: '', email: '', telefon: '', empresa: '', notes: '' });
    const [editSponsorLoading, setEditSponsorLoading] = useState(false);
    const [editSponsorError, setEditSponsorError] = useState(null);

    // Upgrade subscription modal state
    const [upgradingSubscription, setUpgradingSubscription] = useState(null); // { subscriptionId, currentTierId, sponsorName }
    const [upgradeTargetTier, setUpgradeTargetTier] = useState(null);
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [upgradeError, setUpgradeError] = useState(null);

    // Fetch monthly revenue data
    const fetchMonthlyRevenue = useCallback(() => {
        fetchAPI('/sponsors/monthly-revenue', (data) => {
            if (data?.success) {
                setMonthlyRevenue(data);
            }
        });
    }, []);

    // Fetch sponsor tiers
    const fetchTiers = useCallback(() => {
        setLoading(true);
        setError(null);
        fetchAPI('/sponsors/tiers', (data) => {
            if (data?.success) {
                setTiers(data.tiers || []);
            } else {
                setError(data?.message || 'Error carregant els tiers de patrocinadors');
            }
            setLoading(false);
        }, (err) => {
            setError('Error de connexió');
            setLoading(false);
        });
    }, []);

    // Fetch subscriptions for a specific tier
    const fetchSubscriptions = useCallback((tierId) => {
        setLoadingSubscriptions(prev => ({ ...prev, [tierId]: true }));
        fetchAPI(`/sponsors/tier/${tierId}`, (data) => {
            if (data?.success) {
                setTierSubscriptions(prev => ({ ...prev, [tierId]: data.subscriptions || [] }));
            }
            setLoadingSubscriptions(prev => ({ ...prev, [tierId]: false }));
        }, () => {
            setLoadingSubscriptions(prev => ({ ...prev, [tierId]: false }));
        });
    }, []);

    // Fetch sponsor history
    const fetchHistory = useCallback((page = 0) => {
        setHistoryLoading(true);
        const offset = page * HISTORY_PAGE_SIZE;
        fetchAPI(`/sponsors/history?limit=${HISTORY_PAGE_SIZE}&offset=${offset}`, (data) => {
            if (data?.success) {
                setHistoryEvents(data.events || []);
                setHistoryTotal(data.total || 0);
                setHistoryPage(page);
            }
            setHistoryLoading(false);
        }, () => {
            setHistoryLoading(false);
        });
    }, []);

    useEffect(() => {
        fetchMonthlyRevenue();
        fetchTiers();
    }, [fetchMonthlyRevenue, fetchTiers]);

    // Expand all tiers by default when they are loaded
    useEffect(() => {
        if (tiers.length > 0) {
            setExpandedTier(prev => {
                const newSet = new Set(prev);
                tiers.forEach(tier => {
                    newSet.add(tier.id);
                });
                // Fetch subscriptions for newly expanded tiers
                tiers.forEach(tier => {
                    if (!prev.has(tier.id) && !tierSubscriptions[tier.id]) {
                        fetchSubscriptions(tier.id);
                    }
                });
                return newSet;
            });
        }
    }, [tiers]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch history when showing history section
    useEffect(() => {
        if (showHistory && historyEvents.length === 0) {
            fetchHistory(0);
        }
    }, [showHistory, historyEvents.length, fetchHistory]);

    // Create new tier
    const handleCreateTier = useCallback(() => {
        if (!tierForm.nom || tierForm.nom.trim() === '') {
            setTierFormError('El nom és obligatori');
            return;
        }
        
        const importValue = parseFloat(tierForm.import);
        if (isNaN(importValue) || importValue <= 0) {
            setTierFormError('L\'import ha de ser un número positiu');
            return;
        }

        setTierFormLoading(true);
        setTierFormError(null);

        const tierData = {
            nom: tierForm.nom.trim(),
            descripcio: tierForm.descripcio?.trim() || null,
            import: importValue,
            cadence: tierForm.cadence,
            commission_mode: tierForm.commission_mode
        };

        postAPI('/sponsors/tiers', tierData, (data) => {
            if (data?.success) {
                setCreateSuccess(`Tier "${tierForm.nom.trim()}" creat correctament!`);
                setTierForm({ nom: '', descripcio: '', import: '', commission_mode: 'colla_absorbs', cadence: 'monthly' });
                setShowCreateTierForm(false);
                fetchTiers();
                fetchMonthlyRevenue();
                setTimeout(() => setCreateSuccess(null), 5000);
            } else {
                setTierFormError(data?.message || 'Error creant el tier');
            }
            setTierFormLoading(false);
        }, (err) => {
            setTierFormError('Error de connexió');
            setTierFormLoading(false);
        });
    }, [tierForm, fetchTiers, fetchMonthlyRevenue]);

    // Toggle expanded tier
    const toggleTierExpanded = (tierId) => {
        setExpandedTier(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tierId)) {
                newSet.delete(tierId);
            } else {
                newSet.add(tierId);
                if (!tierSubscriptions[tierId]) {
                    fetchSubscriptions(tierId);
                }
            }
            return newSet;
        });
    };

    // Generate payment link for a tier (friendly URL that redirects to Stripe checkout)
    const handleGenerateLink = (tierId, tierName, e) => {
        e?.stopPropagation();
        
        // Find tier details
        const tier = tiers.find(t => t.id === tierId);
        const cadenceOption = tier ? getCadenceOption(tier.cadence) : getCadenceOption('monthly');
        
        // Generate friendly URL
        const friendlyLink = `https://${window.location.hostname}/nou-patrocinador?tier=${tierId}`;
        
        setPaymentLinkModal({
            visible: true,
            link: friendlyLink,
            tierName: tierName,
            amount: tier ? parseFloat(tier.import) : null,
            cadenceLabel: cadenceOption.perLabel
        });
    };

    // Admin actions for subscriptions
    const handlePause = async (subscriptionId, tierId) => {
        if (!window.confirm('Segur que vols pausar aquesta subscripció?')) return;
        
        setActionLoading(subscriptionId);
        postAPI(`/sponsors/subscriptions/${subscriptionId}/pause`, { performedBy: userInfo?.id }, (data) => {
            if (data?.success) {
                fetchSubscriptions(tierId);
                fetchMonthlyRevenue();
                fetchHistory(0);
            } else {
                alert(data?.message || 'Error pausant la subscripció');
            }
            setActionLoading(null);
        }, () => {
            alert('Error de connexió');
            setActionLoading(null);
        });
    };

    const handleResume = async (subscriptionId, tierId) => {
        setActionLoading(subscriptionId);
        postAPI(`/sponsors/subscriptions/${subscriptionId}/resume`, { performedBy: userInfo?.id }, (data) => {
            if (data?.success) {
                fetchSubscriptions(tierId);
                fetchMonthlyRevenue();
                fetchHistory(0);
            } else {
                alert(data?.message || 'Error reactivant la subscripció');
            }
            setActionLoading(null);
        }, () => {
            alert('Error de connexió');
            setActionLoading(null);
        });
    };

    const handleCancel = async (subscriptionId, tierId) => {
        if (!window.confirm('Segur que vols cancel·lar aquesta subscripció? Aquesta acció no es pot desfer.')) return;
        
        setActionLoading(subscriptionId);
        postAPI(`/sponsors/subscriptions/${subscriptionId}/cancel`, { performedBy: userInfo?.id }, (data) => {
            if (data?.success) {
                fetchSubscriptions(tierId);
                fetchMonthlyRevenue();
                fetchTiers();
                fetchHistory(0);
            } else {
                alert(data?.message || 'Error cancel·lant la subscripció');
            }
            setActionLoading(null);
        }, () => {
            alert('Error de connexió');
            setActionLoading(null);
        });
    };

    // Upgrade subscription to a different tier
    const handleStartUpgrade = (sub, currentTierId) => {
        setUpgradingSubscription({ subscriptionId: sub.id, currentTierId, sponsorName: sub.nom });
        setUpgradeTargetTier(null);
        setUpgradeError(null);
    };

    const handleConfirmUpgrade = () => {
        if (!upgradeTargetTier || !upgradingSubscription) return;

        setUpgradeLoading(true);
        setUpgradeError(null);
        postAPI(`/sponsors/subscriptions/${upgradingSubscription.subscriptionId}/upgrade`, {
            newTierId: upgradeTargetTier,
            performedBy: userInfo?.id
        }, (data) => {
            if (data?.success) {
                // Refresh both old and new tier subscriptions
                fetchSubscriptions(upgradingSubscription.currentTierId);
                const newTierId = parseInt(upgradeTargetTier);
                if (expandedTier.has(newTierId)) {
                    fetchSubscriptions(newTierId);
                }
                fetchMonthlyRevenue();
                fetchTiers();
                fetchHistory(0);
                setUpgradingSubscription(null);
            } else {
                setUpgradeError(data?.message || 'Error canviant de tier');
            }
            setUpgradeLoading(false);
        }, () => {
            setUpgradeError('Error de connexió');
            setUpgradeLoading(false);
        });
    };

    // Edit tier name
    const handleStartEditTier = (tier, e) => {
        e.stopPropagation();
        setEditingTierId(tier.id);
        setEditTierName(tier.nom);
    };

    const handleCancelEditTier = (e) => {
        e.stopPropagation();
        setEditingTierId(null);
        setEditTierName('');
    };

    const handleSaveEditTier = (tierId, e) => {
        e.stopPropagation();
        if (!editTierName.trim()) return;

        setEditTierLoading(true);
        putAPI(`/sponsors/tiers/${tierId}`, { nom: editTierName.trim() }, (data) => {
            if (data?.success) {
                setTiers(prev => 
                    prev.map(t => t.id === tierId ? { ...t, nom: editTierName.trim() } : t)
                );
                setEditingTierId(null);
                setEditTierName('');
            } else {
                alert(data?.message || 'Error actualitzant el nom');
            }
            setEditTierLoading(false);
        }, () => {
            alert('Error de connexió');
            setEditTierLoading(false);
        });
    };

    // Delete tier
    const handleDeleteTier = (tier, e) => {
        e.stopPropagation();

        const totalActive = (tier.active_sponsors || 0) + (tier.pending_sponsors || 0) + (tier.paused_sponsors || 0);
        if (totalActive > 0) {
            alert('No es pot eliminar un tier amb patrocinadors actius o pendents');
            return;
        }

        if (!window.confirm(`Segur que vols eliminar el tier "${tier.nom}"? Aquesta acció no es pot desfer.`)) {
            return;
        }

        setDeleteTierLoading(tier.id);
        deleteAPI(`/sponsors/tiers/${tier.id}`, (data) => {
            if (data?.success) {
                fetchTiers();
                fetchMonthlyRevenue();
            } else {
                alert(data?.message || 'Error eliminant el tier');
            }
            setDeleteTierLoading(null);
        }, () => {
            alert('Error de connexió');
            setDeleteTierLoading(null);
        });
    };

    // Start editing a sponsor (from subscriptions table)
    const handleStartEditSponsor = (sub) => {
        setEditingSponsor(sub);
        setEditSponsorForm({
            nom: sub.nom || '',
            email: sub.email || '',
            telefon: sub.telefon || '',
            empresa: sub.empresa || '',
            notes: sub.notes || ''
        });
        setEditSponsorError(null);
    };

    // Save edited sponsor
    const handleSaveEditSponsor = useCallback(() => {
        if (!editSponsorForm.nom || editSponsorForm.nom.trim() === '') {
            setEditSponsorError('El nom és obligatori');
            return;
        }

        setEditSponsorLoading(true);
        setEditSponsorError(null);

        const updateData = {
            nom: editSponsorForm.nom.trim(),
            email: editSponsorForm.email?.trim() || null,
            telefon: editSponsorForm.telefon?.trim() || null,
            empresa: editSponsorForm.empresa?.trim() || null,
            notes: editSponsorForm.notes?.trim() || null
        };

        putAPI(`/sponsors/${editingSponsor.sponsor_id}`, updateData, (data) => {
            if (data?.success) {
                setEditingSponsor(null);
                setEditSponsorForm({ nom: '', email: '', telefon: '', empresa: '', notes: '' });
                // Refresh subscriptions for all expanded tiers
                expandedTier.forEach(tierId => {
                    fetchSubscriptions(tierId);
                });
            } else {
                setEditSponsorError(data?.message || 'Error actualitzant el patrocinador');
            }
            setEditSponsorLoading(false);
        }, () => {
            setEditSponsorError('Error de connexió');
            setEditSponsorLoading(false);
        });
    }, [editSponsorForm, editingSponsor, expandedTier, fetchSubscriptions]);

    // Copy payment link to clipboard
    const handleCopyLink = () => {
        navigator.clipboard.writeText(paymentLinkModal.link).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ca-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Get status badge
    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className="sponsor-badge active">Activa</span>;
            case 'pending':
                return <span className="sponsor-badge pending">Pendent</span>;
            case 'paused':
                return <span className="sponsor-badge paused">Pausada</span>;
            case 'cancelled':
                return <span className="sponsor-badge cancelled">Cancel·lada</span>;
            case 'past_due':
                return <span className="sponsor-badge past-due">Pagament pendent</span>;
            default:
                return <span className="sponsor-badge">{status}</span>;
        }
    };

    // Get event type badge
    const getEventInfo = (eventType) => {
        switch (eventType) {
            case 'payment_link_sent':
            case 'payment_link_emailed':
                return { badge: <span className="event-badge link-sent"><i className="fas fa-envelope"></i> Link enviat per email</span> };
            case 'payment_link_generated':
                return { badge: <span className="event-badge link-generated"><i className="fas fa-link"></i> Link generat</span> };
            case 'subscribed':
                return { badge: <span className="event-badge subscribed"><i className="fas fa-user-plus"></i> Alta</span> };
            case 'paused':
                return { badge: <span className="event-badge paused"><i className="fas fa-pause-circle"></i> Pausa</span> };
            case 'resumed':
                return { badge: <span className="event-badge resumed"><i className="fas fa-play-circle"></i> Reactivada</span> };
            case 'cancelled':
                return { badge: <span className="event-badge cancelled"><i className="fas fa-user-times"></i> Baixa</span> };
            case 'upgraded':
                return { badge: <span className="event-badge upgraded"><i className="fas fa-arrow-up"></i> Canvi de tier</span> };
            default:
                return { badge: <span className="event-badge">{eventType}</span> };
        }
    };

    // Format datetime
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ca-ES', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Chart data for monthly revenue
    const monthNames = ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'];
    
    const chartData = useMemo(() => {
        if (!monthlyRevenue?.monthlyData) return null;
        
        const data = monthlyRevenue.monthlyData;
        
        const labels = data.map(d => {
            const [year, month] = d.month.split('-');
            return `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`;
        });
        
        const revenues = data.map(d => d.total);
        
        return {
            labels,
            datasets: [{
                label: 'Ingressos mensuals',
                data: revenues,
                backgroundColor: data.map(d => {
                    if (d.isFuture) return 'rgba(34, 197, 94, 0.4)';
                    if (d.isCurrent && d.isProjected) return 'rgba(34, 197, 94, 0.6)';
                    return 'rgba(34, 197, 94, 0.85)';
                }),
                borderColor: data.map(d => {
                    if (d.isProjected) return 'rgba(34, 197, 94, 0.6)';
                    return 'rgba(34, 197, 94, 1)';
                }),
                borderWidth: 2,
                borderRadius: 6,
                maxBarThickness: 50
            }],
            rawData: data
        };
    }, [monthlyRevenue]);
    
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        onResize: treasuryChartResize,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#475569',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    title: (contexts) => {
                        const ctx = contexts[0];
                        const dataPoint = chartData?.rawData?.[ctx.dataIndex];
                        if (!dataPoint) return '';
                        const [year, month] = dataPoint.month.split('-');
                        return `${monthNames[parseInt(month) - 1]} ${year}`;
                    },
                    label: (context) => {
                        const dataPoint = chartData?.rawData?.[context.dataIndex];
                        const value = context.parsed.y || 0;
                        const label = dataPoint?.isProjected ? 'Projectat' : 'Confirmat';
                        return `${label}: ${formatMoney(value)}€`;
                    },
                    afterLabel: (context) => {
                        const dataPoint = chartData?.rawData?.[context.dataIndex];
                        if (!dataPoint) return '';
                        if (dataPoint.count > 0) {
                            return `${dataPoint.count} pagament${dataPoint.count > 1 ? 's' : ''}`;
                        }
                        return '';
                    }
                }
            },
            datalabels: {
                display: 'auto',
                anchor: 'end',
                align: 'top',
                formatter: (value) => value > 0 ? `${value.toFixed(0)}€` : '',
                color: '#475569',
                font: { size: 11, weight: '600' },
                padding: 4
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    color: '#64748b',
                    font: { size: 11, weight: '500' }
                }
            },
            y: {
                beginAtZero: true,
                grid: { color: '#e2e8f0' },
                border: { display: false },
                ticks: {
                    color: '#64748b',
                    font: { size: 11 },
                    padding: 8,
                    callback: (value) => formatMoney(value) + '€'
                }
            }
        },
        layout: { padding: { top: 30 } }
    }), [chartData]);

    if (loading) {
        return (
            <div className="sponsors-tab">
                <div className="sponsors-loading">
                    <i className="fas fa-spinner fa-spin"></i> Carregant patrocinadors...
                </div>
            </div>
        );
    }

    return (
        <div className="sponsors-tab">
            <h2>Patrocinadors</h2>
            <p className="sponsors-description">
                Gestiona els patrocinadors externs de la colla amb pagaments recurrents per web.
            </p>

            {error ? (
                <div className="sponsors-error">
                    <i className="fas fa-exclamation-triangle"></i> {error}
                    <button onClick={fetchTiers} className="retry-button">
                        Tornar a provar
                    </button>
                </div>
            ) : null}

            {/* Success message */}
            {createSuccess ? (
                <div className="create-success-message">
                    <i className="fas fa-check-circle"></i> {createSuccess}
                </div>
            ) : null}

            {/* Create new tier */}
            {can(userInfo, 'MANAGE_QUOTES') ? (
                <div className="create-tier-section">
                    {!stripeConnectStatus?.verified ? (
                        <div className="stripe-required-notice">
                            <i className="fas fa-info-circle"></i>
                            <span>Per crear tiers de patrocinadors, primer has d'activar els pagaments amb targeta a la pestanya de "Pagaments".</span>
                        </div>
                    ) : showCreateTierForm ? (
                        <div className="create-tier-form">
                            <div className="create-tier-header">
                                <div className="create-tier-icon">
                                    <i className="fas fa-medal"></i>
                                </div>
                                <div>
                                    <h3>Nou tier de patrocini</h3>
                                    <p className="create-tier-subtitle">Defineix un nivell de patrocini amb preu i freqüència</p>
                                </div>
                                <button
                                    className="close-form-btn"
                                    onClick={() => {
                                        setShowCreateTierForm(false);
                                        setTierForm({ nom: '', descripcio: '', import: '', commission_mode: 'colla_absorbs', cadence: 'monthly' });
                                        setTierFormError(null);
                                    }}
                                    disabled={tierFormLoading}
                                    title="Tancar"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            {tierFormError ? (
                                <div className="create-form-error">
                                    <i className="fas fa-exclamation-circle"></i> {tierFormError}
                                </div>
                            ) : null}

                            <div className="create-form-fields">
                                {/* Section 1: Basic Info */}
                                <div className="form-section">
                                    <div className="form-section-title">
                                        <span className="section-number">1</span>
                                        <span>Informació bàsica</span>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-field flex-2">
                                            <label htmlFor="tier-name">Nom del tier <span className="required">*</span></label>
                                            <input
                                                id="tier-name"
                                                type="text"
                                                placeholder="Ex: Bronze, Plata, Or"
                                                value={tierForm.nom}
                                                onChange={(e) => {
                                                    setTierForm(prev => ({ ...prev, nom: e.target.value }));
                                                    setTierFormError(null);
                                                }}
                                                disabled={tierFormLoading}
                                            />
                                        </div>
                                        <div className="form-field flex-3">
                                            <label htmlFor="tier-descripcio">Descripció <span className="optional">(opcional)</span></label>
                                            <input
                                                id="tier-descripcio"
                                                type="text"
                                                placeholder="Ex: Inclou logo a la web i menció a xarxes"
                                                value={tierForm.descripcio}
                                                onChange={(e) => setTierForm(prev => ({ ...prev, descripcio: e.target.value }))}
                                                disabled={tierFormLoading}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Section 2: Pricing */}
                                <div className="form-section">
                                    <div className="form-section-title">
                                        <span className="section-number">2</span>
                                        <span>Preu i freqüència</span>
                                    </div>
                                    <div className="pricing-row">
                                        <div className="form-field import-field">
                                            <label htmlFor="tier-import">Import <span className="required">*</span></label>
                                            <div className="import-input-wrapper">
                                                <input
                                                    id="tier-import"
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="100"
                                                    value={tierForm.import}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^\d*[.,]?\d*$/.test(value)) {
                                                            setTierForm(prev => ({ ...prev, import: value.replace(',', '.') }));
                                                            setTierFormError(null);
                                                        }
                                                    }}
                                                    disabled={tierFormLoading}
                                                />
                                                <span className="currency-symbol">€</span>
                                            </div>
                                        </div>
                                        <div className="cadence-selector">
                                            {CADENCE_OPTIONS.map(option => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    className={`cadence-btn ${tierForm.cadence === option.value ? 'selected' : ''}`}
                                                    onClick={() => setTierForm(prev => ({ ...prev, cadence: option.value }))}
                                                    disabled={tierFormLoading}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {tierForm.import ? (
                                        <div className="pricing-preview">
                                            <i className="fas fa-receipt"></i>
                                            <span>El patrocinador pagarà <strong>{formatMoney(tierForm.import)}€</strong> cada {getCadenceOption(tierForm.cadence).months === 1 ? 'mes' : `${getCadenceOption(tierForm.cadence).months} mesos`}</span>
                                        </div>
                                    ) : null}
                                </div>

                                {/* Section 3: Commission */}
                                <div className="form-section">
                                    <div className="form-section-title">
                                        <span className="section-number">3</span>
                                        <span>Gestió de comissions</span>
                                        <span className="commission-rate">Stripe: 4% + 0,3€</span>
                                    </div>
                                    <div className="commission-cards">
                                        <label className={`commission-card ${tierForm.commission_mode !== 'colla_absorbs' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="commission_mode"
                                                value="user_pays"
                                                checked={tierForm.commission_mode !== 'colla_absorbs'}
                                                onChange={() => setTierForm(prev => ({ ...prev, commission_mode: 'user_pays' }))}
                                                disabled={tierFormLoading}
                                            />
                                            <div className="commission-checkbox">
                                                <i className="fas fa-check"></i>
                                            </div>
                                            <div className="commission-card-text">
                                                <strong>El patrocinador paga la comissió</strong>
                                                <span>Import final = tier + comissió. La colla rep l'import íntegre.</span>
                                            </div>
                                        </label>
                                        <label className={`commission-card ${tierForm.commission_mode === 'colla_absorbs' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="commission_mode"
                                                value="colla_absorbs"
                                                checked={tierForm.commission_mode === 'colla_absorbs'}
                                                onChange={() => setTierForm(prev => ({ ...prev, commission_mode: 'colla_absorbs' }))}
                                                disabled={tierFormLoading}
                                            />
                                            <div className="commission-checkbox">
                                                <i className="fas fa-check"></i>
                                            </div>
                                            <div className="commission-card-text">
                                                <strong>La colla absorbeix la comissió</strong>
                                                <span>El patrocinador paga només l'import del tier. La colla rep menys.</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="create-form-actions">
                                <button 
                                    className="create-btn"
                                    onClick={handleCreateTier}
                                    disabled={tierFormLoading || !tierForm.nom.trim() || !tierForm.import}
                                >
                                    {tierFormLoading ? (
                                        <><i className="fas fa-spinner fa-spin"></i> Creant...</>
                                    ) : (
                                        <><i className="fas fa-plus"></i> Crear tier</>
                                    )}
                                </button>
                                <button
                                    className="cancel-btn"
                                    onClick={() => {
                                        setShowCreateTierForm(false);
                                        setTierForm({ nom: '', descripcio: '', import: '', commission_mode: 'colla_absorbs', cadence: 'monthly' });
                                        setTierFormError(null);
                                    }}
                                    disabled={tierFormLoading}
                                >
                                    Cancel·lar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            className="add-tier-btn"
                            onClick={() => setShowCreateTierForm(true)}
                        >
                            <i className="fas fa-plus-circle"></i> Crear nou tier de patrocini
                        </button>
                    )}
                </div>
            ) : null}

            {/* Monthly Revenue Chart */}
            {chartData ? (
                <div className="sponsors-revenue-chart">
                    <div className="chart-header">
                        <h3><i className="fas fa-chart-bar"></i> Ingressos per patrocinis</h3>
                        {monthlyRevenue?.activeSponsors > 0 ? (
                            <div className="chart-summary">
                                <span className="summary-item">
                                    <i className="fas fa-handshake"></i>
                                    {monthlyRevenue.activeSponsors} patrocinador{monthlyRevenue.activeSponsors !== 1 ? 's' : ''} actiu{monthlyRevenue.activeSponsors !== 1 ? 's' : ''}
                                </span>
                                <span className="summary-item projected">
                                    <i className="fas fa-arrow-trend-up"></i>
                                    {formatMoney(monthlyRevenue.projectedMonthly * 12)}€/any estimat
                                </span>
                            </div>
                        ) : null}
                    </div>
                    <div className="chart-container">
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                    <div className="chart-legend">
                        <span className="legend-item confirmed"><span className="legend-color"></span> Confirmat</span>
                        <span className="legend-item projected"><span className="legend-color"></span> Projectat</span>
                    </div>
                </div>
            ) : null}

            {/* Tiers List */}
            <div className="sponsors-section">
                <h3>Tiers de patrocini</h3>
                
                {tiers.length === 0 ? (
                    <div className="sponsors-empty">
                        <i className="fas fa-handshake"></i>
                        <p>No hi ha tiers de patrocini creats.</p>
                        {can(userInfo, 'MANAGE_QUOTES') ? (
                            <p className="hint">Utilitza el botó de dalt per crear el primer tier de patrocini.</p>
                        ) : (
                            <p className="hint">Contacta amb un cap de junta per crear tiers de patrocini.</p>
                        )}
                    </div>
                ) : (
                    <div className="tier-list">
                        {tiers.map(tier => (
                            <div key={tier.id} className="tier-card">
                                <div 
                                    className="tier-header"
                                    onClick={() => editingTierId !== tier.id ? toggleTierExpanded(tier.id) : undefined}
                                >
                                    <div className="tier-info">
                                        {editingTierId === tier.id ? (
                                            <div className="edit-title-row" onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="text"
                                                    className="edit-title-input"
                                                    value={editTierName}
                                                    onChange={(e) => setEditTierName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveEditTier(tier.id, e);
                                                        if (e.key === 'Escape') handleCancelEditTier(e);
                                                    }}
                                                    disabled={editTierLoading}
                                                    autoFocus
                                                />
                                                <button
                                                    className="edit-action-btn save"
                                                    onClick={(e) => handleSaveEditTier(tier.id, e)}
                                                    disabled={editTierLoading || !editTierName.trim()}
                                                    title="Desar"
                                                >
                                                    {editTierLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                                                </button>
                                                <button
                                                    className="edit-action-btn cancel"
                                                    onClick={handleCancelEditTier}
                                                    disabled={editTierLoading}
                                                    title="Cancel·lar"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="title-row">
                                                <h4>{tier.nom}</h4>
                                                {can(userInfo, 'MANAGE_QUOTES') ? (
                                                    <>
                                                        <button
                                                            className="edit-title-btn"
                                                            onClick={(e) => handleStartEditTier(tier, e)}
                                                            title="Editar nom"
                                                        >
                                                            <i className="fas fa-pencil-alt"></i>
                                                        </button>
                                                        <button
                                                            className="delete-tier-btn"
                                                            onClick={(e) => handleDeleteTier(tier, e)}
                                                            disabled={(tier.active_sponsors || 0) + (tier.pending_sponsors || 0) + (tier.paused_sponsors || 0) > 0 || deleteTierLoading === tier.id}
                                                            title={(tier.active_sponsors || 0) + (tier.pending_sponsors || 0) > 0
                                                                ? 'No es pot eliminar amb patrocinadors actius'
                                                                : 'Eliminar tier'}
                                                        >
                                                            {deleteTierLoading === tier.id
                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                : <i className="fas fa-trash-alt"></i>}
                                                        </button>
                                                    </>
                                                ) : null}
                                            </div>
                                        )}
                                        <span className="tier-amount">{formatMoney(tier.import)}€{getCadenceOption(tier.cadence).perLabel}</span>
                                        {tier.descripcio ? <span className="tier-description">{tier.descripcio}</span> : null}
                                    </div>
                                    <div className="tier-actions-row">
                                        {can(userInfo, 'MANAGE_QUOTES') && stripeConnectStatus?.verified ? (
                                            <button
                                                className="generate-link-btn"
                                                onClick={(e) => handleGenerateLink(tier.id, tier.nom, e)}
                                                disabled={generateLinkLoading === tier.id}
                                                title="Copiar enllaç de pagament"
                                            >
                                                {generateLinkLoading === tier.id ? (
                                                    <><i className="fas fa-spinner fa-spin"></i> Copiant...</>
                                                ) : (
                                                    <><i className="fas fa-link"></i> Copiar enllaç</>
                                                )}
                                            </button>
                                        ) : null}
                                        <div className="tier-stats">
                                            <span className="sponsors-count active">
                                                <i className="fas fa-user-check"></i> {tier.active_sponsors || 0}
                                            </span>
                                            <span className="sponsors-count paused">
                                                <i className="fas fa-user-clock"></i> {tier.paused_sponsors || 0}
                                            </span>
                                        </div>
                                        <i className={`fas fa-chevron-${expandedTier.has(tier.id) ? 'up' : 'down'} expand-icon`}></i>
                                    </div>
                                </div>
                                
                                {expandedTier.has(tier.id) ? (
                                    <div className="tier-sponsors">
                                        {loadingSubscriptions[tier.id] ? (
                                            <div className="loading-sponsors">
                                                <i className="fas fa-spinner fa-spin"></i> Carregant patrocinadors...
                                            </div>
                                        ) : (() => {
                                            const activeSubscriptions = tierSubscriptions[tier.id]?.filter(sub => sub.status !== 'cancelled') || [];
                                            return activeSubscriptions.length === 0 ? (
                                                <div className="no-sponsors">
                                                    <i className="fas fa-users-slash"></i>
                                                    <p>Aquest tier encara no té patrocinadors.</p>
                                                    <p className="hint">Genera un link de pagament i comparteix-lo amb potencials patrocinadors.</p>
                                                </div>
                                            ) : (
                                                <TreasuryTable label="Patrocinadors" className="sponsors-table-wrapper">
                                                    <table className="sponsors-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Patrocinador</th>
                                                                <th>Email</th>
                                                                <th>Estat</th>
                                                                <th>Data inici</th>
                                                                <th>Pròxim pagament</th>
                                                                {can(userInfo, 'MANAGE_QUOTES') ? <th>Accions</th> : null}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {activeSubscriptions.map(sub => (
                                                            <tr key={sub.id} className={`status-${sub.status}`}>
                                                                <td className="sponsor-name">
                                                                    {sub.nom}
                                                                    {sub.empresa ? <span className="sponsor-empresa">({sub.empresa})</span> : null}
                                                                </td>
                                                                <td className="sponsor-email">{sub.email}</td>
                                                                <td>{getStatusBadge(sub.status)}</td>
                                                                <td>{formatDate(sub.start_date)}</td>
                                                                <td>{formatDate(sub.next_payment_date)}</td>
                                                                {can(userInfo, 'MANAGE_QUOTES') ? (
                                                                    <td className="sponsors-actions-cell">
                                                                        <button
                                                                            className="action-btn edit"
                                                                            onClick={() => handleStartEditSponsor(sub)}
                                                                            title="Editar patrocinador"
                                                                        >
                                                                            <i className="fas fa-pencil-alt"></i>
                                                                        </button>
                                                                        {(sub.status === 'active' || sub.status === 'paused') && tiers.filter(t => t.id !== tier.id).length > 0 ? (
                                                                            <button
                                                                                className="action-btn upgrade"
                                                                                onClick={() => handleStartUpgrade(sub, tier.id)}
                                                                                disabled={actionLoading === sub.id}
                                                                                title="Canviar de tier"
                                                                            >
                                                                                <i className="fas fa-arrow-up"></i>
                                                                            </button>
                                                                        ) : null}
                                                                        {sub.status === 'active' ? (
                                                                            <button
                                                                                className="action-btn pause"
                                                                                onClick={() => handlePause(sub.id, tier.id)}
                                                                                disabled={actionLoading === sub.id}
                                                                                title="Pausar subscripció"
                                                                            >
                                                                                {actionLoading === sub.id 
                                                                                    ? <i className="fas fa-spinner fa-spin"></i>
                                                                                    : <i className="fas fa-pause"></i>}
                                                                            </button>
                                                                        ) : sub.status === 'paused' ? (
                                                                            <button
                                                                                className="action-btn resume"
                                                                                onClick={() => handleResume(sub.id, tier.id)}
                                                                                disabled={actionLoading === sub.id}
                                                                                title="Reactivar subscripció"
                                                                            >
                                                                                {actionLoading === sub.id 
                                                                                    ? <i className="fas fa-spinner fa-spin"></i>
                                                                                    : <i className="fas fa-play"></i>}
                                                                            </button>
                                                                        ) : null}
                                                                        {sub.status !== 'cancelled' ? (
                                                                            <button
                                                                                className="action-btn cancel"
                                                                                onClick={() => handleCancel(sub.id, tier.id)}
                                                                                disabled={actionLoading === sub.id}
                                                                                title="Cancel·lar subscripció"
                                                                            >
                                                                                {actionLoading === sub.id 
                                                                                    ? <i className="fas fa-spinner fa-spin"></i>
                                                                                    : <i className="fas fa-times"></i>}
                                                                            </button>
                                                                        ) : null}
                                                                    </td>
                                                                ) : null}
                                                            </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </TreasuryTable>
                                            );
                                        })()}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* History Section */}
            {can(userInfo, 'MANAGE_QUOTES') ? (
                <div className="sponsors-section history-section">
                    <div 
                        className="history-header"
                        onClick={() => setShowHistory(!showHistory)}
                    >
                        <h3>
                            <i className="fas fa-history"></i> Historial de canvis
                        </h3>
                        <i className={`fas fa-chevron-${showHistory ? 'up' : 'down'} expand-icon`}></i>
                    </div>
                    
                    {showHistory ? (
                        <div className="history-content">
                            {historyLoading ? (
                                <div className="history-loading">
                                    <i className="fas fa-spinner fa-spin"></i> Carregant historial...
                                </div>
                            ) : historyEvents.length === 0 ? (
                                <div className="history-empty">
                                    <i className="fas fa-info-circle"></i>
                                    <p>No hi ha registres d'historial encara.</p>
                                </div>
                            ) : (
                                <>
                                    <TreasuryTable label="Historial de patrocinis" className="history-table-container">
                                        <table className="sponsors-table history-table">
                                            <thead>
                                                <tr>
                                                    <th>Patrocinador</th>
                                                    <th>Acció</th>
                                                    <th>Tier</th>
                                                    <th>Data</th>
                                                    <th>Realitzat per</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historyEvents.map((event) => {
                                                    const eventInfo = getEventInfo(event.event_type);
                                                    const sponsorName = event.sponsor_empresa 
                                                        ? `${event.sponsor_nom} (${event.sponsor_empresa})`
                                                        : (event.sponsor_nom || '-');
                                                    
                                                    const performedByName = event.performed_by_nom 
                                                        ? `${event.performed_by_nom} ${event.performed_by_cognom}`
                                                        : '-';

                                                    return (
                                                        <tr key={event.id}>
                                                            <td className="sponsor-name">{sponsorName}</td>
                                                            <td>
                                                                {eventInfo.badge}
                                                                {event.event_type === 'upgraded' && event.notes ? (
                                                                    <span className="event-notes">{event.notes}</span>
                                                                ) : null}
                                                            </td>
                                                            <td className="history-tier-cell">
                                                                <i className="fas fa-medal" style={{ marginRight: '6px', color: '#64748b' }}></i>
                                                                {event.tier_nom}
                                                            </td>
                                                            <td className="history-date-cell">
                                                                {formatDateTime(event.created_at)}
                                                            </td>
                                                            <td className="history-user-cell">
                                                                {event.performed_by_nom ? (
                                                                    <>
                                                                        <i className="fas fa-user-shield" style={{ marginRight: '6px', color: '#94a3b8' }}></i>
                                                                        {performedByName}
                                                                    </>
                                                                ) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </TreasuryTable>
                                    
                                    {/* Pagination */}
                                    {historyTotal > HISTORY_PAGE_SIZE ? (
                                        <div className="history-pagination">
                                            <button
                                                className="pagination-btn"
                                                onClick={() => fetchHistory(historyPage - 1)}
                                                disabled={historyPage === 0 || historyLoading}
                                            >
                                                <i className="fas fa-chevron-left"></i> Anterior
                                            </button>
                                            <span className="pagination-info">
                                                {historyPage * HISTORY_PAGE_SIZE + 1} - {Math.min((historyPage + 1) * HISTORY_PAGE_SIZE, historyTotal)} de {historyTotal}
                                            </span>
                                            <button
                                                className="pagination-btn"
                                                onClick={() => fetchHistory(historyPage + 1)}
                                                disabled={(historyPage + 1) * HISTORY_PAGE_SIZE >= historyTotal || historyLoading}
                                            >
                                                Següent <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* Edit Sponsor Modal */}
            {editingSponsor ? (
                <TreasuryDialog label="Editar patrocinador" className="sponsor-modal" onClose={() => {
                    setEditingSponsor(null);
                    setEditSponsorForm({ nom: '', email: '', telefon: '', empresa: '', notes: '' });
                    setEditSponsorError(null);
                }} closeDisabled={editSponsorLoading}
                >
                    <div className="modal-header">
                        <h3><i className="fas fa-user-edit"></i> Editar patrocinador</h3>
                        <button className="close-button" aria-label="Tancar" disabled={editSponsorLoading} onClick={() => {
                            setEditingSponsor(null);
                            setEditSponsorForm({ nom: '', email: '', telefon: '', empresa: '', notes: '' });
                            setEditSponsorError(null);
                        }}
                        >×
                        </button>
                    </div>
                    <div className="modal-content treasury-dialog-body">
                        {editSponsorError ? (
                            <div className="modal-error">
                                <i className="fas fa-exclamation-circle"></i> {editSponsorError}
                            </div>
                        ) : null}
                        <div className="sponsor-modal-form">
                            <div className="form-row">
                                <div className="form-field">
                                    <label>Nom *</label>
                                    <input
                                        type="text"
                                        placeholder="Nom del patrocinador"
                                        value={editSponsorForm.nom}
                                        onChange={(e) => {
                                            setEditSponsorForm(prev => ({ ...prev, nom: e.target.value }));
                                            setEditSponsorError(null);
                                        }}
                                        disabled={editSponsorLoading}
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        placeholder="correu@exemple.com"
                                        value={editSponsorForm.email}
                                        onChange={(e) => {
                                            setEditSponsorForm(prev => ({ ...prev, email: e.target.value }));
                                            setEditSponsorError(null);
                                        }}
                                        disabled={editSponsorLoading}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-field">
                                    <label>Telèfon</label>
                                    <input
                                        type="text"
                                        placeholder="Opcional"
                                        value={editSponsorForm.telefon}
                                        onChange={(e) => setEditSponsorForm(prev => ({ ...prev, telefon: e.target.value }))}
                                        disabled={editSponsorLoading}
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Empresa</label>
                                    <input
                                        type="text"
                                        placeholder="Opcional"
                                        value={editSponsorForm.empresa}
                                        onChange={(e) => setEditSponsorForm(prev => ({ ...prev, empresa: e.target.value }))}
                                        disabled={editSponsorLoading}
                                    />
                                </div>
                            </div>
                            <div className="form-field full-width">
                                <label>Notes internes</label>
                                <textarea
                                    placeholder="Opcional"
                                    value={editSponsorForm.notes}
                                    onChange={(e) => setEditSponsorForm(prev => ({ ...prev, notes: e.target.value }))}
                                    disabled={editSponsorLoading}
                                    rows={3}
                                />
                            </div>
                        </div>

                    </div>
                    <div className="modal-actions treasury-dialog-footer">
                        <button
                            className="create-btn"
                            onClick={handleSaveEditSponsor}
                            disabled={editSponsorLoading}
                        >
                            {editSponsorLoading ? (
                                <><i className="fas fa-spinner fa-spin"></i> Desant...</>
                            ) : (
                                <><i className="fas fa-save"></i> Desar canvis</>
                            )}
                        </button>
                        <button
                            className="cancel-btn"
                            onClick={() => {
                                setEditingSponsor(null);
                                setEditSponsorForm({ nom: '', email: '', telefon: '', empresa: '', notes: '' });
                                setEditSponsorError(null);
                            }}
                            disabled={editSponsorLoading}
                        >
                            Cancel·lar
                        </button>
                    </div>
                </TreasuryDialog>
            ) : null}

            {/* Upgrade Tier Modal */}
            {upgradingSubscription ? (
                <TreasuryDialog label="Canviar de tier" className="sponsor-modal" onClose={() => setUpgradingSubscription(null)} closeDisabled={upgradeLoading}>
                    <div className="modal-header">
                        <h3><i className="fas fa-arrow-up"></i> Canviar de tier</h3>
                        <button className="close-button" aria-label="Tancar" disabled={upgradeLoading} onClick={() => setUpgradingSubscription(null)}>×</button>
                    </div>
                    <div className="modal-content treasury-dialog-body">
                        {upgradeError ? (
                            <div className="modal-error">
                                <i className="fas fa-exclamation-circle"></i> {upgradeError}
                            </div>
                        ) : null}
                        <p>Selecciona el nou tier per a <strong>{upgradingSubscription.sponsorName}</strong>:</p>
                        <div className="upgrade-tier-list">
                            {tiers.filter(t => t.id !== upgradingSubscription.currentTierId).map(t => {
                                const cadence = getCadenceOption(t.cadence);
                                return (
                                    <div
                                        key={t.id}
                                        className={`upgrade-tier-option ${upgradeTargetTier === t.id ? 'selected' : ''}`}
                                        onClick={() => setUpgradeTargetTier(t.id)}
                                    >
                                        <div className="upgrade-tier-info">
                                            <strong>{t.nom}</strong>
                                            <span className="upgrade-tier-price">{formatMoney(t.import)}€{cadence.perLabel}</span>
                                        </div>
                                        {t.descripcio ? <p className="upgrade-tier-desc">{t.descripcio}</p> : null}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="modal-hint">
                            <i className="fas fa-info-circle"></i> El canvi és immediat. Si el nou tier és més car, es cobrarà la diferència proporcional al proper pagament. Si és més barat, es descomptarà.
                        </p>

                    </div>
                    <div className="modal-actions treasury-dialog-footer">
                        <button
                            className="create-btn"
                            onClick={handleConfirmUpgrade}
                            disabled={!upgradeTargetTier || upgradeLoading}
                        >
                            {upgradeLoading ? (
                                <><i className="fas fa-spinner fa-spin"></i> Canviant...</>
                            ) : (
                                <><i className="fas fa-arrow-up"></i> Confirmar canvi</>
                            )}
                        </button>
                        <button
                            className="cancel-btn"
                            onClick={() => setUpgradingSubscription(null)}
                            disabled={upgradeLoading}
                        >
                            Cancel·lar
                        </button>
                    </div>
                </TreasuryDialog>
            ) : null}

            {/* Payment Link Modal */}
            {paymentLinkModal.visible ? (
                <TreasuryDialog label="Enllaç de pagament" className="payment-link-modal" onClose={() => setPaymentLinkModal({ visible: false, link: '', tierName: '' })}>
                    <div className="modal-header">
                        <h3><i className="fas fa-link"></i> Enllaç de pagament per al patrocinador</h3>
                        <button className="close-button" aria-label="Tancar" onClick={() => setPaymentLinkModal({ visible: false, link: '', tierName: '' })}>×</button>
                    </div>
                    <div className="modal-content treasury-dialog-body">
                        <p>Enllaç de pagament per al tier <strong>{paymentLinkModal.tierName}</strong>:</p>
                        <div className="payment-link-box">
                            <input
                                type="text"
                                value={paymentLinkModal.link}
                                readOnly
                                onClick={(e) => e.target.select()}
                            />
                            <button
                                className={`copy-btn ${copySuccess ? 'success' : ''}`}
                                onClick={handleCopyLink}
                            >
                                {copySuccess ? (
                                    <><i className="fas fa-check"></i> Copiat!</>
                                ) : (
                                    <><i className="fas fa-copy"></i> Copiar</>
                                )}
                            </button>
                        </div>
                        <p className="modal-hint">
                            <i className="fas fa-info-circle"></i>
                            Comparteix aquest link per WhatsApp, email o on vulguis. Quan el patrocinador pagui, apareixerà automàticament al dashboard.
                        </p>
                    </div>
                </TreasuryDialog>
            ) : null}
        </div>
    );
}

export default SponsorsTab;
